import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

// ---------------------------------------------------------------------------
// useRangeDrag — a generic press-drag "sweep a contiguous range" gesture, shared
// by the scheduler's area boards (TaskProgressSection's status board and
// TaskPanelLocationsTab's assignment map). Both surfaces rhyme: press a cell to
// anchor, drag to extend the range across a flattened, board-ordered index list,
// release to commit the whole range as ONE batch.
//
// This hook owns the mechanics that are identical across the two boards:
//   · anchor / current indices (state for rendering the live preview + a ref
//     mirror so the window listeners always read the freshest range),
//   · the window pointerup / pointercancel listeners, LIFECYCLE-MANAGED by an
//     effect: both are registered when a stroke starts and removed TOGETHER on
//     commit, cancel, OR unmount. (The previous imperative `{ once: true }`
//     pairing leaked the pointercancel listener after every normal drag and left
//     listeners dangling if the drawer closed mid-drag — a stale commit/cancel
//     could then fire against a live or torn-down stroke.)
//   · the touch `releasePointerCapture` workaround (touch implicitly captures
//     the pointer to the press target, which would suppress pointerenter on
//     sibling cells — release it so the range tracks),
//   · the "suppress the trailing click after a TRAVELLED stroke" flag: a plain
//     tap commits a 1-cell no-op stroke and lets the trailing click do the real
//     work; a stroke that actually moved swallows its trailing click once. A
//     fresh pointerdown always re-arms.
//
// `TMeta` is whatever the calling board needs to carry from press to commit —
// the status board passes the anchor cell's LocationStatus; the assignment tab
// passes its intent ('add' | 'remove') plus an optional origin floor id. The
// board keeps its own preview / floor-header logic; the hook only generalizes
// range tracking, listener lifecycle, and click suppression.
// ---------------------------------------------------------------------------

export interface IRangeStroke<TMeta> {
  /** Index the press started on (board-ordered position). */
  anchor: number;
  /** Index the pointer is currently over. */
  current: number;
  /** Caller payload captured at press, replayed at commit. */
  meta: TMeta;
}

export interface IUseRangeDragOptions<TMeta> {
  /** Called once on release with the inclusive [lo, hi] range and the press meta. */
  onCommit: (lo: number, hi: number, meta: TMeta) => void;
}

export interface IUseRangeDrag<TMeta> {
  /** The live stroke while a press-drag is in progress, else null. */
  stroke: IRangeStroke<TMeta> | null;
  /** Anchor a stroke at `index` (primary button / touch / pen only). */
  startStroke: (e: ReactPointerEvent<HTMLElement>, index: number, meta: TMeta) => void;
  /** Extend the live stroke's current edge to `index` (no-op if unchanged). */
  extendStroke: (index: number) => void;
  /** True once after a travelled stroke — read-and-consume in the click handler. */
  shouldSuppressClick: () => boolean;
}

export function useRangeDrag<TMeta>({ onCommit }: IUseRangeDragOptions<TMeta>): IUseRangeDrag<TMeta> {
  const [stroke, setStroke] = useState<IRangeStroke<TMeta> | null>(null);
  const strokeRef = useRef<IRangeStroke<TMeta> | null>(null);
  // Set true by commit when a stroke actually travelled — the browser then fires
  // a trailing click on the anchor, which the click handler must NOT act on.
  const suppressClickRef = useRef(false);

  // Always call the freshest onCommit at release time. Nothing dispatches
  // mid-stroke (extending only moves the preview), so this is equivalent to the
  // old "bind the render that started the stroke" behaviour, minus the staleness.
  // Synced in an effect (not during render) so it's fresh before any pointer
  // event fires — effects flush ahead of the next discrete input.
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  const write = useCallback((next: IRangeStroke<TMeta> | null) => {
    strokeRef.current = next;
    setStroke(next);
  }, []);

  const startStroke = useCallback(
    (e: ReactPointerEvent<HTMLElement>, index: number, meta: TMeta) => {
      if (e.button !== 0) return; // primary mouse button / touch / pen only
      suppressClickRef.current = false; // a fresh press always re-arms the tap
      write({ anchor: index, current: index, meta });
      // Touch implicitly captures the pointer to the press target, which would
      // suppress pointerenter on sibling cells — release it so the stroke tracks.
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* nothing captured */ }
    },
    [write],
  );

  const extendStroke = useCallback(
    (index: number) => {
      const current = strokeRef.current;
      if (!current || current.current === index) return;
      write({ ...current, current: index });
    },
    [write],
  );

  const shouldSuppressClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  // Window listeners live for exactly the lifetime of a stroke. Keyed on whether
  // a stroke is active (NOT its identity), so extending the range doesn't churn
  // listeners; the cleanup removes BOTH on commit, cancel, or unmount.
  const active = stroke !== null;
  useEffect(() => {
    if (!active) return;
    const commit = () => {
      const s = strokeRef.current;
      write(null);
      if (!s) return;
      suppressClickRef.current = s.anchor !== s.current;
      const lo = Math.min(s.anchor, s.current);
      const hi = Math.max(s.anchor, s.current);
      onCommitRef.current(lo, hi, s.meta);
    };
    const cancel = () => { write(null); };
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [active, write]);

  return { stroke, startStroke, extendStroke, shouldSuppressClick };
}
